// ============================================
// COMPONENT - Modal Pagar Fiado
// ============================================

import { useState } from 'react';
import { MdClose, MdPayment, MdPerson, MdPhone } from 'react-icons/md';
import type { FiadoHistorico } from '@almacenes/core';
import { capitalize, formatPhoneNumber } from '@almacenes/ui';
import { formatearMonto, obtenerValorNumerico } from '../formatoMonto';

interface ModalPagarFiadoProps {
  fiado: FiadoHistorico;
  onClose: () => void;
  onPagar: (tipoPago: 'EFECTIVO' | 'DEBITO' | 'CREDITO' | 'TRANSFERENCIA', monto: number) => void;
  isPaying: boolean;
}

export const ModalPagarFiado: React.FC<ModalPagarFiadoProps> = ({
  fiado,
  onClose,
  onPagar,
  isPaying,
}) => {
  const [tipoPago, setTipoPago] = useState<'EFECTIVO' | 'DEBITO' | 'CREDITO' | 'TRANSFERENCIA'>('EFECTIVO');
  const [montoInput, setMontoInput] = useState('');

  const handleMontoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const valorFormateado = formatearMonto(e.target.value);
    setMontoInput(valorFormateado);
  };

  const handlePagarTodo = () => {
    setMontoInput(formatearMonto(fiado.saldoPendiente.toString()));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const montoNumerico = obtenerValorNumerico(montoInput);

    if (montoNumerico <= 0) {
      alert('El monto debe ser mayor a 0');
      return;
    }

    if (montoNumerico > fiado.saldoPendiente) {
      alert('El monto no puede ser superior al saldo pendiente');
      return;
    }

    onPagar(tipoPago, montoNumerico);
  };

  const montoNumerico = obtenerValorNumerico(montoInput);
  const montoValido = montoNumerico > 0 && montoNumerico <= fiado.saldoPendiente;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <MdPayment className="text-2xl text-green-600" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#11194F]">Pagar Fiado</h3>
                <p className="text-sm text-gray-600">Boleta #{fiado.boletaId}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={isPaying}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            >
              <MdClose className="text-2xl text-gray-600" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {/* Información del cliente */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-gray-700">
                <MdPerson className="text-gray-400" />
                <span className="font-medium">{capitalize(fiado.aliasNombre || fiado.clienteNombre)}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600 text-sm">
                <MdPhone className="text-gray-400" />
                <span>{formatPhoneNumber(fiado.clienteTelefono)}</span>
              </div>
            </div>

            {/* Resumen de montos */}
            <div className="space-y-2">
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-gray-600">Total Fiado:</span>
                <span className="font-semibold text-gray-900">
                  ${fiado.montoTotal.toLocaleString('es-CL')}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-200">
                <span className="text-gray-600">Monto Abonado:</span>
                <span className="font-semibold text-green-600">
                  ${fiado.montoAbonado.toLocaleString('es-CL')}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-gray-600">Saldo Pendiente:</span>
                <span className="font-bold text-red-600 text-lg">
                  ${fiado.saldoPendiente.toLocaleString('es-CL')}
                </span>
              </div>
            </div>

            {/* Tipo de pago */}
            <div>
              <label className="block text-sm font-medium text-[#11194F] mb-2">
                Tipo de Pago <span className="text-red-500">*</span>
              </label>
              <select
                value={tipoPago}
                onChange={(e) => setTipoPago(e.target.value as any)}
                disabled={isPaying}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent outline-none disabled:opacity-50 disabled:bg-gray-50"
              >
                <option value="EFECTIVO">Efectivo</option>
                <option value="DEBITO">Débito</option>
                <option value="CREDITO">Crédito</option>
                <option value="TRANSFERENCIA">Transferencia</option>
              </select>
            </div>

            {/* Monto a pagar */}
            <div>
              <label className="block text-sm font-medium text-[#11194F] mb-2">
                Monto a Pagar <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <input
                    type="text"
                    value={montoInput}
                    onChange={handleMontoChange}
                    placeholder="0"
                    disabled={isPaying}
                    className="w-full pl-7 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#43BFFF] focus:border-transparent outline-none disabled:opacity-50 disabled:bg-gray-50"
                  />
                </div>
                <button
                  type="button"
                  onClick={handlePagarTodo}
                  disabled={isPaying}
                  className="px-4 py-2 bg-blue-100 text-[#43BFFF] rounded-lg hover:bg-blue-200 transition-colors disabled:opacity-50 whitespace-nowrap"
                >
                  Pagar Todo
                </button>
              </div>
              {montoNumerico > 0 && montoNumerico > fiado.saldoPendiente && (
                <p className="text-xs text-red-500 mt-1">
                  El monto no puede ser superior al saldo pendiente
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isPaying}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isPaying || !montoValido}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isPaying ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Procesando...
                </>
              ) : (
                <>
                  <MdPayment />
                  Confirmar Pago
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
